import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsSelectState = 'default' | 'error';

export interface DsSelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'ds-input-select',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ds-select" [class.ds-select--error]="state === 'error'" [class.ds-select--disabled]="disabled"
         [attr.aria-disabled]="disabled || null">

      @if (label) {
        <label class="ds-select__label" [attr.for]="selectId">{{ label }}</label>
      }

      <div class="ds-select__field-box">
        <select
          class="ds-select__select"
          [attr.id]="selectId"
          [disabled]="disabled"
          [attr.aria-invalid]="state === 'error' ? 'true' : null"
          [attr.aria-describedby]="showHelpText && helpText ? helpId : null"
          (change)="onChange($event)"
          (focus)="focused.emit($event)"
          (blur)="blurred.emit($event)"
        >
          <option value="" [disabled]="true" [selected]="!value">{{ placeholder }}</option>
          @for (opt of options; track opt.value) {
            <option [value]="opt.value" [selected]="opt.value === value">{{ opt.label }}</option>
          }
        </select>
        <span class="ds-select__arrow">
          <ds-icon name="keyboard_arrow_down" size="16px"></ds-icon>
        </span>
      </div>

      @if (showHelpText && helpText) {
        <span class="ds-select__help-text" [attr.id]="helpId"
              [attr.role]="state === 'error' ? 'alert' : null">{{ helpText }}</span>
      }

    </div>
  `,
  styles: [`
    :host { display: block; }

    /* ── Container ───────────────────────────────────────────── */
    .ds-select {
      display:        flex;
      flex-direction: column;
      gap:            var(--ds-input-gap);
      min-width:      200px;
    }

    .ds-select--disabled {
      opacity:        0.44;
      pointer-events: none;
    }

    /* ── Label ───────────────────────────────────────────────── */
    .ds-select__label {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-label-font-size);
      line-height: var(--ds-input-label-line-height);
      color:       var(--ds-input-label-color);
    }

    /* ── Field box ───────────────────────────────────────────── */
    .ds-select__field-box {
      position:      relative;
      display:       flex;
      align-items:   center;
      height:        var(--ds-input-field-height);
      background:    var(--ds-input-background);
      border:        var(--ds-input-border-width) solid var(--ds-input-border-color);
      border-radius: var(--ds-input-border-radius);
      transition:    border-color 120ms ease;
      box-sizing:    border-box;
    }

    .ds-select--error .ds-select__field-box {
      background:   var(--ds-input-background-error);
      border-color: var(--ds-input-border-color-error);
    }

    .ds-select__field-box:focus-within {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    /* ── Native select ───────────────────────────────────────── */
    .ds-select__select {
      appearance:    none;
      flex:          1 1 0;
      height:        100%;
      padding:       var(--ds-input-padding-y) var(--ds-input-padding-x);
      padding-right: calc(var(--ds-input-padding-x) + var(--ds-input-arrow-size) + var(--ds-input-icon-gap));
      border:        none;
      outline:       none;
      background:    transparent;
      font-family:   'Sura Sans', system-ui, sans-serif;
      font-weight:   400;
      font-size:     var(--ds-input-field-font-size);
      line-height:   var(--ds-input-field-line-height);
      color:         var(--ds-input-value-color);
      cursor:        pointer;
      box-sizing:    border-box;
      width:         100%;
    }

    .ds-select__select option[value=""] { color: var(--ds-input-placeholder-color); }

    /* ── Chevron ─────────────────────────────────────────────── */
    .ds-select__arrow {
      position:       absolute;
      right:          var(--ds-input-padding-x);
      top:            50%;
      transform:      translateY(-50%);
      pointer-events: none;
      color:          var(--ds-input-arrow-color);
      display:        flex;
      align-items:    center;
    }

    /* ── Help text ───────────────────────────────────────────── */
    .ds-select__help-text {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-help-font-size);
      line-height: var(--ds-input-help-line-height);
      color:       var(--ds-input-help-color);
    }

    .ds-select--error .ds-select__help-text {
      color: var(--ds-input-help-color-error);
    }
  `],
})
export class DsInputSelectComponent {
  private static _counter = 0;

  readonly selectId = `ds-input-select-${++DsInputSelectComponent._counter}`;
  readonly helpId   = `${this.selectId}-help`;

  @Input() options:      DsSelectOption[] = [];
  @Input() value:        string           = '';
  @Input() label:        string           = '';
  @Input() placeholder:  string           = 'Selecciona una opción';
  @Input() helpText:     string           = '';
  @Input() showHelpText: boolean          = false;
  @Input() state:        DsSelectState    = 'default';
  @Input() disabled:     boolean          = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() focused     = new EventEmitter<FocusEvent>();
  @Output() blurred     = new EventEmitter<FocusEvent>();

  onChange(event: Event): void {
    this.valueChange.emit((event.target as HTMLSelectElement).value);
  }
}
